import { useState, useRef, useCallback } from "react";

interface UseUndoRedoOptions {
  maxHistory?: number;
}

interface UseUndoRedoResult<T> {
  state: T;
  setState: React.Dispatch<React.SetStateAction<T>>;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  reset: (value: T) => void;
}

export function useUndoRedo<T>(
  initialValue: T | (() => T),
  options: UseUndoRedoOptions = {},
): UseUndoRedoResult<T> {
  const { maxHistory = 50 } = options;

  const [state, setStateRaw] = useState<T>(initialValue);
  const undoStack = useRef<T[]>([]);
  const redoStack = useRef<T[]>([]);

  // Force re-render when stacks change so canUndo/canRedo update
  const [, setTick] = useState(0);
  const tick = useCallback(() => setTick((t) => t + 1), []);

  const setState: React.Dispatch<React.SetStateAction<T>> = useCallback(
    (update) => {
      setStateRaw((prev) => {
        const next = typeof update === "function"
          ? (update as (prev: T) => T)(prev)
          : update;
        undoStack.current = [
          ...undoStack.current.slice(-(maxHistory - 1)),
          prev,
        ];
        redoStack.current = [];
        return next;
      });
      tick();
    },
    [maxHistory, tick],
  );

  const undo = useCallback(() => {
    if (undoStack.current.length === 0) return;
    setStateRaw((current) => {
      const previous = undoStack.current.pop()!;
      redoStack.current.push(current);
      return previous;
    });
    tick();
  }, [tick]);

  const redo = useCallback(() => {
    if (redoStack.current.length === 0) return;
    setStateRaw((current) => {
      const next = redoStack.current.pop()!;
      undoStack.current.push(current);
      return next;
    });
    tick();
  }, [tick]);

  const reset = useCallback((value: T) => {
    setStateRaw(value);
    undoStack.current = [];
    redoStack.current = [];
    tick();
  }, [tick]);

  return {
    state,
    setState,
    undo,
    redo,
    canUndo: undoStack.current.length > 0,
    canRedo: redoStack.current.length > 0,
    reset,
  };
}
